import { useCallback, useEffect, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useUIStore } from '@/stores/uiStore';
import { useConversationsStore } from '@/stores/conversationsStore';
import { useProjectsStore } from '@/stores/projectsStore';
import {
  RouteType,
  parseHash,
  generateHash,
  isOAuthRedirect,
} from '@/lib/hashRouter';

/**
 * Hook to sync hash routing with Zustand stores
 * Provides navigation function and handles hash change events
 */
export function useHashRouter() {
  const { setCurrentView, setSelectedNavItem } = useUIStore();
  const { setChatPageSelectedConversationId, setCurrentConversationId } =
    useConversationsStore();
  const { setCurrentProjectId } = useProjectsStore();

  // Track if we're currently navigating to prevent loops
  const isNavigatingRef = useRef(false);

  /**
   * Apply a route to the Zustand stores
   * Uses batched updates to prevent multiple re-renders
   */
  const applyRoute = useCallback(
    (route: RouteType) => {
      // Batch all state updates to prevent multiple re-renders
      unstable_batchedUpdates(() => {
        switch (route.type) {
          case 'chat':
            setCurrentView('chat');
            setSelectedNavItem('');
            setChatPageSelectedConversationId(route.conversationId);
            setCurrentConversationId(route.conversationId); // 🔥 同步两个状态
            setCurrentProjectId(null);
            break;

          case 'chats': {
            setCurrentView('chats-list');
            // 🔥 在回调执行时获取最新的 tab 值，而不是使用闭包捕获的旧值
            const currentTab = useUIStore.getState().chatsListInitialTab;
            setSelectedNavItem(
              currentTab === 'groupChats' ? 'groupChats' : 'chats'
            );
            setChatPageSelectedConversationId(undefined);
            setCurrentConversationId(undefined); // 🔥 同步两个状态
            setCurrentProjectId(null);
            break;
          }

          case 'projects':
            setCurrentView('projects-list');
            setSelectedNavItem('projects');
            setChatPageSelectedConversationId(undefined);
            setCurrentConversationId(undefined); // 🔥 同步两个状态
            setCurrentProjectId(null);
            break;

          case 'project':
            setCurrentView('project-detail');
            setSelectedNavItem('');
            setChatPageSelectedConversationId(undefined);
            setCurrentConversationId(undefined); // 🔥 同步两个状态
            setCurrentProjectId(route.projectId);
            break;

          case 'project-chat':
            setCurrentView('chat');
            setSelectedNavItem('');
            setChatPageSelectedConversationId(route.conversationId);
            setCurrentConversationId(route.conversationId); // 🔥 同步两个状态
            setCurrentProjectId(route.projectId);
            break;

          case 'project-pdfs':
            setCurrentView('pdf-list');
            setSelectedNavItem('');
            setChatPageSelectedConversationId(undefined);
            setCurrentConversationId(undefined); // 🔥 同步两个状态
            setCurrentProjectId(route.projectId);
            break;

          case 'project-members':
            setCurrentView('project-members');
            setSelectedNavItem('');
            setChatPageSelectedConversationId(undefined);
            setCurrentConversationId(undefined); // 🔥 同步两个状态
            setCurrentProjectId(route.projectId);
            break;

          case 'agents':
            setCurrentView('agents-list');
            setSelectedNavItem('agents');
            setChatPageSelectedConversationId(undefined);
            setCurrentConversationId(undefined); // 🔥 同步两个状态
            setCurrentProjectId(null);
            break;

          case 'shared':
            setCurrentView('shared-list');
            setSelectedNavItem('shared');
            setChatPageSelectedConversationId(undefined);
            setCurrentConversationId(undefined); // 🔥 同步两个状态
            setCurrentProjectId(null);
            break;

          case 'user-settings':
            setCurrentView('user-settings');
            setSelectedNavItem('');
            setChatPageSelectedConversationId(undefined);
            setCurrentConversationId(undefined);
            setCurrentProjectId(null);
            break;
        }
      });
    },
    [
      setCurrentView,
      setSelectedNavItem,
      setChatPageSelectedConversationId,
      setCurrentConversationId,
      setCurrentProjectId,
    ]
  );

  /**
   * Navigate to a route by updating the hash
   * This will trigger the hashchange listener which applies the route
   */
  const navigate = useCallback((route: RouteType) => {
    const hash = generateHash(route);
    if (window.location.hash !== hash) {
      isNavigatingRef.current = true;
      window.location.hash = hash;
    }
  }, []);

  /**
   * Handle hash change events
   */
  const handleHashChange = useCallback(() => {
    // Skip if OAuth redirect is in progress
    if (isOAuthRedirect()) {
      return;
    }

    const route = parseHash(window.location.hash);
    if (route) {
      applyRoute(route);
    } else {
      // Invalid hash, redirect to chat
      navigate({ type: 'chat' });
    }

    isNavigatingRef.current = false;
  }, [applyRoute, navigate]);

  /**
   * Initialize hash routing on mount
   */
  useEffect(() => {
    // 始终注册 hashchange 事件监听器，确保登录成功后能正确处理路由
    window.addEventListener('hashchange', handleHashChange);

    // 只在非 OAuth 重定向时应用初始路由
    // OAuth 重定向时跳过，让登录流程完成后再处理路由
    if (!isOAuthRedirect()) {
      // Apply initial route from hash
      const route = parseHash(window.location.hash);
      if (route) {
        applyRoute(route);
      } else if (!window.location.hash || window.location.hash === '#') {
        // Set default hash if none exists
        window.location.hash = '#/chat';
      }
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [applyRoute, handleHashChange]);

  return { navigate };
}

/**
 * Get the navigate function without the full hook overhead
 * Useful for calling from outside React components
 */
export function hashNavigate(route: RouteType): void {
  const hash = generateHash(route);
  if (window.location.hash !== hash) {
    window.location.hash = hash;
  }
}
